import React, { useState, useEffect, useMemo } from "react";
import { useInspectProgress } from "../../../../hooks/useInspectProgress";
import { useInspections } from "../../../../hooks/useInspections";
import { useHints } from "../../../../hooks/useHints";
import InspectProgressChip from "../../../../components/ui/InspectProgressChip";
import { useMissionGuide } from "../../../../hooks/useMissionGuide";
import { useScenarioTracker } from "../../../../hooks/useScenarioTracker";
import { getInspectKeys } from "../../../../data/inspectables";
import RaisedHandsBadge from "../../../../components/ui/RaisedHandsBadge";
import { useToast } from "../../../../components/ui/Toast";
import { ModalRegister } from "../../../../components/ui/ModalRegister";
import EmployeeElimination from "../../../origami/components/puzzles/EmployeeElimination";
import PlanningBoard from "../../../origami/components/puzzles/PlanningBoard";
import NotebookClues from "../../../origami/components/puzzles/NotebookClues";
import PostItClue from "../../../origami/components/puzzles/PostitClue";
import TerminalUnlock from "../../../origami/components/ui/TerminalUnlock";
import LaptopUnlock from "../../../origami/components/ui/LaptopUnlock";
import LaptopDesktop from "../../../origami/components/ui/LaptopDesktop";
import IngratitudeTerminal from "../../../origami/components/ui/IngratitudeTerminal";
import { useStage2Reveal } from "../../../../hooks/useStage2Reveal";
import OrigamiCommunicationModal from "../../../origami/components/ui/OrigamiCommunicationModal";
import { stage2RevealText } from "../../../../data/origami/origamiTexts";
import NotebookViewer from "../../../origami/components/puzzles/NotebookViewer";
import HintsModalHub from "../../../../components/ui/HintsModalHub";

const STAGE2_REAL = process.env.REACT_APP_STAGE2_REAL === "true";
const API = process.env.REACT_APP_API_URL;

async function apiUnlockLayer(code, playerId, layerId) {
  const res = await fetch(`${API}/api/sessions/${encodeURIComponent(code)}/layers/unlock-layer`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ playerId: Number(playerId), layerId: Number(layerId) }),
  });
  if (!res.ok) { await res.json().catch(() => null); throw new Error(`unlock-layer failed (${res.status})`); }
  return res.json();
}

function clampRot(r) {
  const x = ((r % 360) + 360) % 360;
  const snaps = [0, 90, 180, 270]; let best = snaps[0]; let bestD = Infinity;
  for (const s of snaps) { const d = Math.abs(s - x); if (d < bestD) { bestD = d; best = s; } }
  return best;
}

const SEGMENTS_LAYER_5 = [[110, 40, 110, 120], [410, 40, 410, 120], [110, 120, 410, 120], [260, 120, 260, 240]];

function Layer5ShapePreview() {
  const centerX = 260; const centerY = 140;
  return (
    <svg viewBox="0 0 520 280" className="w-full h-full">
      <g className="text-indigo-300" transform={`rotate(${clampRot(0)} ${centerX} ${centerY})`} opacity={0.98}
        style={{ filter: "drop-shadow(0 0 4px rgba(0,0,0,0.35)) drop-shadow(0 0 10px rgba(0,0,0,0.22))" }}>
        {SEGMENTS_LAYER_5.map((s, i) => (
          <line key={`5-${i}`} x1={s[0]} y1={s[1]} x2={s[2]} y2={s[3]}
            stroke="currentColor" strokeWidth="6" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        ))}
      </g>
    </svg>
  );
}

function Fragment5FoundPage({ pseudoJ1 }) {
  return (
    <div className="flex flex-col items-center gap-5 py-6">
      <div className="text-sm uppercase tracking-[0.2em] text-slate-400">Élément récupéré</div>
      <div className="w-[320px] h-[320px] bg-black/40 border border-white/10 rounded-xl p-4 flex items-center justify-center"><Layer5ShapePreview /></div>
      <div className="text-lg font-semibold text-white">Vous avez trouvé le fragment 5</div>
      <div className="text-sm text-slate-400 max-w-md text-center">
        {`Le fragment a automatiquement été transmis à ${pseudoJ1} pour compléter la validation Captcha.`}
      </div>
    </div>
  );
}

function PlaceholderStage2_Player5() {
  return (
    <div>
      <h2>🧪 Étape 2 — Joueur 5 - Version Bêta (placeholder)</h2>
      <p className="mt-2">Cette page est <strong>temporaire</strong> pour la bêta.</p>
      <p className="mt-4 text-sm text-slate-500">Si tu restes bloqué(e) : rafraîchis la page (F5).</p>
    </div>
  );
}

const OBSERVATIONS_PAGES = [
  { type: "cover", title: "Observations RH", color: "bg-amber-200" },
  { type: "text", title: "Secret Santa", body: "Idées cadeaux pour l'équipe :\n\n• Marie → Quelque chose pour Nala (son chat)\n  Elle en parle TOUT LE TEMPS\n\n• Thomas → Livre sur la résilience des systèmes\n  (ou un tee-shirt 'Keep Calm and Reboot')\n\n• Samir → Accessoires pour Balthazar\n  Il adore ce chien, c'est mignon" },
  { type: "text", title: "", body: "• Julien → Mug 'Pas de prod le vendredi'\n  (il comprendra l'humour)\n\n• Martin → Thème dark mode personnalisé\n  Apparemment il en collectionne\n\n• Alex → Raquette de tennis + balles\n  Fan de Nadal" },
  { type: "text", title: "Observations d'équipe", body: "• Marie et Martin s'entendent super bien\n  Ils bossent souvent ensemble sur l'UI\n\n• Thomas peut être un peu brusque\n  mais c'est juste sa manière de communiquer\n  Pas méchant, juste direct\n\n• Samir est le médiateur naturel\n  Toujours là pour désamorcer les tensions" },
  { type: "text", title: "Ce qu'on ne dit pas en réunion", body: "• Marie a amené Nala au bureau UNE fois\n  Le chat a dormi sur le clavier de Thomas\n  Thomas a envoyé 'jjjjjjjkkkkkk' dans Slack\n  pendant 20 minutes sans s'en rendre compte\n\n• Julien organise un concours secret\n  de la plus belle plante du bureau\n  Alex a triché en achetant une fausse plante" },
  { type: "text", title: "", body: "• Thomas parle à la machine à café\n  Il lui dit bonjour tous les matins\n  Un jour elle ne marchait pas, il était triste\n  Samir dit que c'était touchant\n\n• Le PDG a un bureau au 3ème étage\n  Personne ne l'a jamais vu dedans\n  Une rumeur dit qu'il y a juste une chaise vide\n  et un portrait de lui qui te fixe\n" },
];

const POSTITS_J5 = [
  { id: 1, color: "bg-yellow-200", text: "Réunion planning hebdo\nmercredi 10h\n— Ne pas oublier !" },
  { id: 2, color: "bg-pink-200", text: "Commande café grain\npour la machine\n(stock presque vide)" },
  { id: 3, color: "bg-blue-200", text: "⚠️ Accès ordinateur portable :\n\nMot de passe récupérable via\nle circuit neuronal de l'empathie\net de la gratitude\n(Laboratoire d'empathie cognitive)" },
  { id: 4, color: "bg-green-200", text: "Penser à valider les congés\nde Julien pour avril\n(déjà reportés 2 fois)" },
];

/**
 * Stage 2 — Joueur 5 — Composant partagé (5J / 6J)
 * N'existe pas en 4J.
 *
 * Props spécifiques :
 *   playerCount {number} — 5 ou 6 → getInspectKeys (seule différence entre variantes)
 */
export default function Stage2Player5Room(props) {
  return STAGE2_REAL ? <RealStage2_Player5 {...props} /> : <PlaceholderStage2_Player5 />;
}

function RealStage2_Player5({
  code, playerId, hintSignal, raisedCount, raisedPlayers, centerMaxH, pseudo, canAdvance, players = [],
  playerCount = 6,
}) {
  const toast = useToast();

  const INSPECT_KEYS = useMemo(
    () => getInspectKeys({ gameId: 1, playerCount, stage: 2, seat: 5 }),
    [playerCount]
  );

  const { markInspectedOnce } = useInspections({ sessionCode: code, stage: 2, playerId: Number(playerId) });
  const { missionUnlock, missionResolve } = useMissionGuide({ code, playerId, stage: 2 });
  const { unlockScenarioStep } = useScenarioTracker({ code });
  const { markHintUsedOnce } = useHints({ sessionCode: code, stage: 2, playerId: Number(playerId) });
  const { inspectedCount, total } = useInspectProgress({ sessionCode: code, stage: 2, playerId: Number(playerId), keys: INSPECT_KEYS });
  const { showStage2Reveal, setShowStage2Reveal } = useStage2Reveal({ code, playerId, stage: 2 });

  const pseudoJ1 = players.find(p => p.pageVariant === 1)?.pseudo || "Joueur 1";

  const [bgReady, setBgReady] = useState(false);
  const [showHintModal, setShowHintModal] = useState(false);
  const [showPlanningModal, setShowPlanningModal] = useState(false);
  const [showNotebook1Modal, setShowNotebook1Modal] = useState(false);
  const [showNotebook2Modal, setShowNotebook2Modal] = useState(false);
  const [showPostItModal, setShowPostItModal] = useState(false);
  const [showEliminationModal, setShowEliminationModal] = useState(false);
  const [showTerminalModal, setShowTerminalModal] = useState(false);
  const [showLaptopModal, setShowLaptopModal] = useState(false);
  const [showFragmentModal, setShowFragmentModal] = useState(false);
  const [showPostits, setShowPostits] = useState(false);
  const [showProcedure, setShowProcedure] = useState(false);
  const [showObservations, setShowObservations] = useState(false);

  const [puzzleSolved, setPuzzleSolved] = useState(() => {
    try { return localStorage.getItem("s2p5_puzzle_solved") === "true"; } catch { return false; }
  });
  const [terminalUnlocked, setTerminalUnlocked] = useState(() => {
    try { return localStorage.getItem("s2p5_terminal_unlocked") === "true"; } catch { return false; }
  });
  const [laptopUnlocked, setLaptopUnlocked] = useState(false);
  const [terminalOpen, setTerminalOpen] = useState(false);
  const [fragmentSent, setFragmentSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [postitIndex, setPostitIndex] = useState(0);

  useEffect(() => { setBgReady(false); }, []);
  useEffect(() => { if (hintSignal > 0) setShowHintModal(true); }, [hintSignal]);

  const handlePuzzleSolved = () => {
    setPuzzleSolved(true);
    missionResolve({ key: "s2.p5.suspects.found" });
    try { localStorage.setItem("s2p5_puzzle_solved", "true"); } catch (e) { console.error(e); }
  };

  const handleIngratitudeComplete = async () => {
    toast?.success?.("Révélation : La source EXECUTIVE est le PDG.");
    try {
      await fetch(`${API}/api/sessions/${code}/stage2-reveal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ playerId: Number(playerId) }),
      });
    } catch (error) { console.error("Error triggering stage2 reveal:", error); }
  };

  const hintCategories = useMemo(() => [
    {
      id: "elimination", title: "Terminal sécurisé", subtitle: "Déverrouiller le terminal",
      hintKeyPrefix: "s2:p5:elimination",
      levels: {
        l1: (<><p className="mb-2">ORIGAMI a généré le dossier MIRAGIO dans la nuit entre minuit et 1h du matin. Il ne peut s'agir que de quelqu'un qui était physiquement au bureau à ce moment-là.</p><p className="text-xs text-gray-300">Deux documents dans la salle te permettent de reconstituer qui était encore présent cette nuit-là.</p></>),
        l2: (<><p className="mb-2">Le planning de la semaine 47 te dit qui était censé être au bureau le jeudi 23. Le carnet de notes de Sophie et le registre de sécurité te disent qui est parti à quelle heure.</p><p className="text-xs text-gray-300">Commence par éliminer ceux qui étaient en déplacement, en congé, en télétravail ou en arrêt maladie ce jour-là.</p></>),
        l3: (<><p className="mb-2">D'après le registre de sécurité (carnet 2), à 20h15 il reste 4 personnes. D'après les départs notés dans le carnet 1, Samir part à 20h45 et Nadia à 21h15. Antoine part à 21h30.</p><p className="text-xs text-gray-300">Il reste donc quelqu'un après Antoine, dont le départ n'est pas enregistré.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Témoin)</p><p className="mt-2 text-xs text-gray-200">La seule personne présente au bureau cette nuit-là sans heure de départ connue est :</p><p className="mt-1 text-sm font-bold text-amber-200">Olivier Garnier — Directeur technique</p><p className="mt-1 font-mono text-lg text-emerald-300 font-bold">ID #272</p><p className="mt-2 text-xs text-gray-300">Saisis cet ID dans le terminal sécurisé pour le déverrouiller et obtenir le fragment 5.</p></>),
      },
    },
    {
      id: "laptop_unlock", title: "Ordinateur portable", subtitle: "Trouver le mot de passe",
      hintKeyPrefix: "s2:p5:laptop",
      levels: {
        l1: (<><p className="mb-2">L'ordinateur portable est protégé par un mot de passe. Un post-it dans cette salle te dit où chercher ce mot de passe.</p><p className="text-xs text-gray-300">Il vient d'une autre salle. Un autre joueur doit te le communiquer.</p></>),
        l2: (<><p>Cherche un post-it bleu parmi les notes murales.</p></>),
        l3: (<><p className="mb-2">Le joueur présent dans le laboratoire d'empathie cognitive doit compléter le circuit neuronal pour que le mot de passe s'affiche dans son terminal de validation. Il doit ensuite te le communiquer.</p><p className="text-xs text-gray-300">S'il a terminé son circuit et que tu n'as pas le mot de passe, demande-lui de le relire dans son terminal de validation (jauge de gratitude à 100%).</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Mot de passe laptop)</p><p className="mt-2 text-xs text-gray-200">Mot de passe de l'ordinateur portable :</p><p className="mt-1 font-mono text-lg text-emerald-300 font-bold">NEURAL</p></>),
      },
    },
    {
      id: "ingratitude_terminal", title: "Terminal ingratitude.exe", subtitle: "Analyse des messages toxiques",
      hintKeyPrefix: "s2:p5:ingratitude",
      levels: {
        l1: (<><p className="mb-2">La première commande dont tu auras besoin pour avancer n'est pas disponible ici.</p><p className="text-xs text-gray-300">Un autre joueur doit te fournir la commande pour exécuter le fichier <span className="font-mono">ingratitude.exe</span>.</p></>),
        l2: (<><p className="mb-2">Deux commandes clés sont nécessaires dans l'ordre. La première charge les messages toxiques, la seconde analyse leur provenance.</p><p className="text-xs text-gray-300">La première commande est stockée dans l'ordinateur de la salle des archives, dans le fichier Protocol.txt, situé dans le dossier .ingratitude.</p></>),
        l3: (<><p className="mb-2">La commande d'analyse de la provenance est à taper après que tous les messages se sont affichés :</p><p className="font-mono text-xs text-cyan-300">Elle est indiquée quand tu tapes la commande "help"</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Terminal ingratitude)</p><p className="mt-2 text-xs text-gray-200">Séquence complète :</p><div className="mt-1 space-y-1 font-mono text-xs text-emerald-300"><p>1. <span className="text-cyan-300">./ingratitude.exe --mode=full --output=console</span></p><p>2. Attendre le chargement de tous les messages</p><p>3. <span className="text-cyan-300">analyze --source</span></p></div><p className="mt-2 text-xs text-gray-300">Le terminal révèle que la source <span className="font-mono">[EXECUTIVE]</span> est le PDG Philippe MARCHAND (ID #001).</p></>),
      },
    },
  ], []);

  return (
    <div className="relative min-h-screen w-full bg-black text-white flex items-center justify-center">
      <div className="relative h-screen w-auto mx-auto aspect-square" style={{ maxHeight: centerMaxH ? `${centerMaxH}px` : undefined }}>
        <div className="absolute right-4 top-4 z-40 flex items-center gap-2 w-fit">
          <InspectProgressChip inspectedCount={inspectedCount} total={total} />
          <RaisedHandsBadge count={raisedCount} players={raisedPlayers} />
        </div>
        <div className="absolute left-4 top-4 rounded-full bg-black/70 border border-white/15 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-slate-200 z-10">
          Salle des Plannings
        </div>

        <img src="/assets/s2-p5-room.webp" alt="Salle des Plannings" className="absolute inset-0 h-full w-full object-contain"
          onLoad={() => setBgReady(true)} onError={() => setBgReady(true)} />

        <div className={["absolute inset-0 transition-opacity duration-200", bgReady ? "opacity-100" : "opacity-0 pointer-events-none"].join(" ")}>
          <img src="/assets/s2-p5-planning.webp" alt="Planning mural"
            onClick={() => { markInspectedOnce('stage2player5:planning'); setShowPlanningModal(true); }}
            className="s2p5-hotspot-planning absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p5-notebook1.webp" alt="Carnet de notes 1"
            onClick={() => { markInspectedOnce('stage2player5:notebook1'); setShowNotebook1Modal(true); }}
            className="s2p5-hotspot-notebook1 absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p5-notebook2.webp" alt="Carnet de notes 2"
            onClick={() => { markInspectedOnce('stage2player5:notebook2'); setShowNotebook2Modal(true); }}
            className="s2p5-hotspot-notebook2 absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p5-postit.webp" alt="Post-it"
            onClick={() => { markInspectedOnce('stage2player5:postit'); setShowPostItModal(true); }}
            className="s2p5-hotspot-postit absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p5-portraits.webp" alt="Portraits employés"
            onClick={() => { markInspectedOnce('stage2player5:portraits'); missionUnlock({ key: "s2.p5.suspects.found", text: `${pseudo} a trouvé une liste des employés suspects.` }); setShowEliminationModal(true); }}
            className="s2p5-hotspot-portraits absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p5-terminal.webp" alt="Terminal sécurisé"
            onClick={() => { markInspectedOnce('stage2player5:terminal'); missionUnlock({ key: "s2.p5.terminal.found", text: `${pseudo} a trouvé un terminal sécurisé verrouillé.` }); setShowTerminalModal(true); }}
            className={`s2p5-hotspot-terminal absolute cursor-pointer transition-all duration-200 ${puzzleSolved ? 'hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]' : 'opacity-50 saturate-50'}`} />
          <img src="/assets/s2-p5-laptop.webp" alt="Ordinateur portable"
            onClick={() => { markInspectedOnce('stage2player5:laptop'); missionUnlock({ key: "s2.p5.laptop.found", text: `${pseudo} a trouvé un ordinateur portable verrouillé.` }); setShowLaptopModal(true); }}
            className="s2p5-hotspot-laptop absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p5-postits.webp" alt="Post-its"
            onClick={() => { markInspectedOnce('stage2player5:postits'); setShowPostits(true); }}
            className="s2p5-hotspot-postits absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p5-procedure.webp" alt="Note de procédure"
            onClick={() => { markInspectedOnce('stage2player5:procedure'); setShowProcedure(true); }}
            className="s2p5-hotspot-procedure absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p5-carnet.webp" alt="Carnet d'observations"
            onClick={() => { markInspectedOnce('stage2player5:carnet'); setShowObservations(true); }}
            className="s2p5-hotspot-carnet absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
        </div>

        <ModalRegister open={showPlanningModal} title="Planning hebdomadaire" onClose={() => setShowPlanningModal(false)} containerClassName="!max-w-[1000px]" onHint={() => setShowHintModal(true)}>
          <PlanningBoard onClose={() => setShowPlanningModal(false)} />
        </ModalRegister>
        <ModalRegister open={showNotebook1Modal} title="Carnet de notes" onClose={() => setShowNotebook1Modal(false)} containerClassName="!max-w-[800px]" onHint={() => setShowHintModal(true)}>
          <NotebookClues notebookId="notebook1" onClose={() => setShowNotebook1Modal(false)} />
        </ModalRegister>
        <ModalRegister open={showNotebook2Modal} title="Carnet de notes" onClose={() => setShowNotebook2Modal(false)} containerClassName="!max-w-[800px]" onHint={() => setShowHintModal(true)}>
          <NotebookClues notebookId="notebook2" onClose={() => setShowNotebook2Modal(false)} />
        </ModalRegister>
        <ModalRegister open={showPostItModal} title="Note" onClose={() => setShowPostItModal(false)} containerClassName="!max-w-[700px]" onHint={() => setShowHintModal(true)}>
          <PostItClue onClose={() => setShowPostItModal(false)} />
        </ModalRegister>

        <ModalRegister open={showEliminationModal} title="Identification du témoin" onClose={() => setShowEliminationModal(false)} containerClassName="!max-w-[1400px]" onHint={() => setShowHintModal(true)}>
          <EmployeeElimination
            sessionCode={code}
            stage={2}
            playerId={playerId}
            onSuccess={handlePuzzleSolved} />
        </ModalRegister>

        <ModalRegister open={showTerminalModal} title="Terminal sécurisé" onClose={() => setShowTerminalModal(false)} containerClassName="!max-w-[800px]" onHint={() => setShowHintModal(true)}>
          <TerminalUnlock
            sessionCode={code}
            stage={2}
            playerId={Number(playerId)}
            alreadyUnlocked={terminalUnlocked}
            onSuccess={async () => {
              setTerminalUnlocked(true);
              missionResolve({ key: "s2.p5.terminal.found" });
              try { localStorage.setItem("s2p5_terminal_unlocked", "true"); } catch (e) { console.error(e); }
              setShowTerminalModal(false);
              setTimeout(() => setShowFragmentModal(true), 500);
              if (fragmentSent || sending) return;
              try {
                setSending(true);
                await apiUnlockLayer(code, playerId, 5);
                missionUnlock({ key: "s2.p5.layer5.found", text: `${pseudo} a identifié le témoin et déverrouillé le fragment 5.` });
                setFragmentSent(true);
              } catch (e) { console.error(e); toast?.error?.("Oups — impossible d'enregistrer le fragment."); }
              finally { setSending(false); }
            }}
          />
        </ModalRegister>

        <ModalRegister
          open={showLaptopModal}
          title={!laptopUnlocked ? "Ordinateur portable" : terminalOpen ? "Terminal — ingratitude.exe" : "Bureau ORIGAMI"}
          onClose={() => setShowLaptopModal(false)}
          containerClassName="!max-w-[1200px]"
          onHint={() => setShowHintModal(true)}
        >
          {!laptopUnlocked &&
            <LaptopUnlock
              sessionCode={code}
              stage={2}
              playerId={Number(playerId)}
              onUnlock={() => setLaptopUnlocked(true)}
              pseudo={pseudo}
              missionUnlock={missionUnlock}
              missionResolve={missionResolve}
            />
          }
          {laptopUnlocked && !terminalOpen && (
            <LaptopDesktop onOpenTerminal={() => { missionUnlock({ key: "s2.p5.audit.terminal.found", text: `${pseudo} a trouvé le terminal d'audit ORIGAMI.` }); setTerminalOpen(true); }} />
          )}
          {laptopUnlocked && terminalOpen && (
            <IngratitudeTerminal onRevealComplete={handleIngratitudeComplete} pseudo={pseudo} missionUnlock={missionUnlock} missionResolve={missionResolve} unlockScenarioStep={unlockScenarioStep} />
          )}
        </ModalRegister>

        <ModalRegister open={showFragmentModal} title="Fragment déverrouillé" onClose={() => setShowFragmentModal(false)} containerClassName="!max-w-[900px]" onHint={() => setShowHintModal(true)}>
          <Fragment5FoundPage pseudoJ1={pseudoJ1} />
          <div className="mt-2 text-center text-xs text-slate-400">{fragmentSent ? "✅ Fragment enregistré et transmis à l'équipe." : "Enregistrement en cours…"}</div>
        </ModalRegister>

        <ModalRegister open={showPostits} title="Notes" onClose={() => setShowPostits(false)} containerClassName="!max-w-[600px]" onHint={() => setShowHintModal(true)}>
          <div className="flex flex-col items-center">
            <div className={`relative p-6 shadow-xl text-black w-64 h-64 ${POSTITS_J5[postitIndex].color}`}>
              <p className="font-handwritten text-base whitespace-pre-wrap">{POSTITS_J5[postitIndex].text}</p>
            </div>
            <div className="flex gap-4 mt-4">
              <button onClick={() => setPostitIndex(i => (i === 0 ? POSTITS_J5.length - 1 : i - 1))}>◀</button>
              <span className="text-sm text-slate-400">{postitIndex + 1} / {POSTITS_J5.length}</span>
              <button onClick={() => setPostitIndex(i => (i === POSTITS_J5.length - 1 ? 0 : i + 1))}>▶</button>
            </div>
          </div>
        </ModalRegister>

        <ModalRegister open={showProcedure} title="Note de procédure" onClose={() => setShowProcedure(false)} containerClassName="!max-w-[700px]" onHint={() => setShowHintModal(true)}>
          <div className="rounded-lg border border-slate-700 bg-black/40 p-6 font-mono text-sm text-slate-200 whitespace-pre-wrap">{`📄 PROCÉDURE INTERNE — MAJ Terminal ORIGAMI\n\nClassification : INTERNE\n\n─────────────────────────────────\n\nNOUVELLES COMMANDES DISPONIBLES\n\nPlusieurs commandes ont été ajoutées au terminal d'audit dans la v2.3.\nPour analyser les données d'interaction employés/IA.\n\n⚠️ ACCÈS RESTREINT\n\nLa procédure complète est stockée sur le serveur\nde la salle des archives.\n\n📁 Dossier protégé par un ou plusieurs mots de passe.`}</div>
        </ModalRegister>

        {/* <ModalRegister open={showObservations} title="Carnet d'observations RH" onClose={() => setShowObservations(false)} containerClassName="!max-w-[900px]" onHint={() => setShowHintModal(true)}>
          <NotebookViewer pages={OBSERVATIONS_PAGES} hintStorageKey={`hint:observations:${code}:p5`} />
        </ModalRegister> */}
        <ModalRegister open={showObservations} title="Carnet d'observations RH" onClose={() => setShowObservations(false)} containerClassName="!max-w-[900px]" onHint={() => setShowHintModal(true)}>
          <NotebookClues notebookId="notebook3" onClose={() => setShowObservations(false)} />
        </ModalRegister>

        <OrigamiCommunicationModal
          open={showStage2Reveal}
          text={stage2RevealText}
          onClose={() => {
            setShowStage2Reveal(false);
            setTimeout(() => { if (canAdvance && typeof canAdvance === 'function') canAdvance(); }, 300);
          }}
          autoClose={false}
        />

        <HintsModalHub open={showHintModal} onClose={() => setShowHintModal(false)}
          title="Indices — Joueur 5 (Salle des Plannings)"
          markHintUsedOnce={markHintUsedOnce} categories={hintCategories}
          usedStorageKey={`hints_used:${code}:s2:p${playerId}`} />
      </div>
    </div>
  );
}